import React, { useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { getStrippedEntityId } from 'lib/entityIds';
import { useForm, useAuth, useCombinedForm, arrayEquals } from 'utils';
import {
  TYPE_COPY_PASTE,
  TYPE_FILE_UPLOAD,
} from 'lib/handleSubmittingCombinedForm';
import CreateCvForm from 'components/CvForm/CreateCvForm';
import CreateApplicantForm from 'components/ApplicantForm/CreateApplicantForm';
import MotivationForm from 'components/MotivationForm';
import CreateEmailAlertForm from 'components/CreateEmailAlertForm';
import PrivacyPolicyForm from 'components/PrivacyPolicyForm';
import Form from 'components/Form';
import Loading from 'components/Loading';
import Error from 'components/Error';
import Fieldset from 'components/Fieldset';
import Checkbox from 'components/Checkbox';

const CombinedForm = ({ translationKey, shouldHideForm, vacancy }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { referrer } = router.query;
  const { user, loading } = useAuth();
  const emailIsValid = true;
  const isApplicationForm = translationKey === 'applicationPage';
  const isUploadCvForm = translationKey === 'uploadCv';
  const isRegisterForm = translationKey === 'register';
  const applicant = user?.applicant;
  const applicantCv = applicant?.cvs?.edges[0]?.node;
  const screeningQuestions = vacancy?.screeningQuestions?.edges;

  const { inputs, handleChange } = useForm({
    name: user?.applicant?.name || '',
    email: user?.email || '',
    phoneNumber: applicant?.phoneNumber || '',
    city: applicant?.address?.city || '',
    description: applicantCv?.description || '',
    desiredPosition: applicantCv?.desiredPosition || '',
    cvUpload: null,
    cvContent: applicantCv?.uploadedFileText || '',
    motivation: vacancy?.companyBranch?.defaultMotivationalText
      ? t('motivation.placeholder', {
          vacancyTitle: vacancy?.title,
          vacancyLocation: vacancy?.address?.city,
          name: applicant?.name || `{${t('name.label')}}`,
        })
      : '',
    screeningQuestions: screeningQuestions || [],
    createEmailAlert: true,
    acceptedPrivacyTerms: false,
  });

  const [createCvType, setCreateCvType] = useState(TYPE_FILE_UPLOAD);
  const [latLong, setLatLong] = useState();
  const [motivationIsPristine, setMotivationIsPristine] = useState(
    !!vacancy?.companyBranch?.defaultMotivationalText
  );
  const [invalidFields, setInvalidFields] = useState([]);

  const {
    submitCombinedForm,
    savingCombinedForm,
    submitErrors,
    uploadCvError,
  } = useCombinedForm({
    user,
    inputs,
    vacancy,
    latLong,
    createCvType,
    callback: () => {
      if (isApplicationForm) {
        // Navigate the user to vacancy confirmation page,
        // either back to the whitelabel site or on this site.
        if (referrer) {
          window.location = `${referrer}/solliciteer/${getStrippedEntityId(
            vacancy.id
          )}/bedankt`;
        } else {
          window.location = `${
            process.env.FRONTEND_ENDPOINT
          }/solliciteer/${getStrippedEntityId(vacancy.id)}/bedankt`;
        }
      }
      if (isUploadCvForm) {
        // Unfortunately this work around is needed for now,
        // this fixes the '[react-google-places-autocomplete]: Google maps places script not loaded' when using router.push
        window.location = `${process.env.FRONTEND_ENDPOINT}/?success=uploadCv`;
      }
      if (isRegisterForm) {
        window.location = `${process.env.FRONTEND_ENDPOINT}/?success=register`;
      }
    },
  });

  const cvIsValid = () =>
    !!applicantCv ||
    (createCvType === TYPE_FILE_UPLOAD && inputs.cvUpload) ||
    (createCvType === TYPE_COPY_PASTE && inputs.cvContent);

  // check if screening questions are filled
  const screeningQuestionsValid = () => {
    if (screeningQuestions) {
      return !screeningQuestions.find(
        ({ node: question }) => question.required && !inputs[question.id]
      );
    }
    return true;
  };

  // Check if the submit button should be disabled or not
  const shouldDisableSubmit = () => {
    const fields = []; // collect all invalid fields in array
    if (!inputs.name) fields.push(t('name.label'));
    if (!inputs.email) fields.push(t('email.label'));
    if (emailIsValid !== true) fields.push(`${t('email.labelInvalid')}`);
    if (!inputs.phoneNumber) fields.push(t('phoneNumber.label'));
    if (!inputs.city) fields.push(t('city.label'));
    if (!cvIsValid()) fields.push(t('curriculumVitae.label'));
    if (isApplicationForm && !inputs.motivation)
      fields.push(t('writeMotivation.label'));
    if (!inputs.acceptedPrivacyTerms) fields.push(t('privacyPolicy.label'));
    if (!screeningQuestionsValid())
      fields.push(t('applicationPage.extraQuestions'));

    if (!arrayEquals(fields, invalidFields)) {
      setInvalidFields(fields);
    }

    return fields.length > 0;
  };

  const handleNameChange = async e => {
    // Add the name to the motivation template if the user has not changed the motivation value
    if (motivationIsPristine) {
      const name = e.target.value || `{${t('name.label')}}`;
      inputs.motivation = t('motivation.placeholder', {
        vacancyTitle: vacancy?.title,
        vacancyLocation: vacancy?.address?.city,
        name,
      });
    }
    return handleChange(e);
  };

  const handleMotivationChange = e => {
    if (motivationIsPristine) {
      setMotivationIsPristine(false);
    }
    return handleChange(e);
  };

  if (!loading && shouldHideForm) return null;

  return (
    <div className="c-combined-form__form c-loading-container u-margin-top-base">
      {(savingCombinedForm || loading) && (
        <Loading extraClasses="c-loading--absolute c-loading--combined-form" />
      )}
      {/* Show all the errors on the top of the form */}
      {!!submitErrors.length && (
        <div className="c-combined-form__form-item c-combined-form__form-item--no-border u-padding-bottom-none">
          {submitErrors.map((error, index) => (
            <Error error={error} key={`${error}-${index}`} />
          ))}
        </div>
      )}
      {!loading && (
        <>
          <div className="c-combined-form__form-item">
            <CreateApplicantForm
              inputs={inputs}
              handleNameChange={handleNameChange}
              onInputChange={handleChange}
              saving={savingCombinedForm}
              setLatLong={setLatLong}
            />
          </div>
          <div className="c-combined-form__form-item">
            <CreateCvForm
              inputs={inputs}
              applicantCv={applicantCv}
              onInputChange={handleChange}
              createCvType={createCvType}
              setCreateCvType={setCreateCvType}
              typeFileUpload={TYPE_FILE_UPLOAD}
              typeCopyPaste={TYPE_COPY_PASTE}
              uploadCvError={uploadCvError}
              disabled={emailIsValid === false}
              saving={savingCombinedForm}
              isApplicationForm={isApplicationForm}
            />
          </div>
          <Form>
            <>
              {isApplicationForm && (
                <>
                  <div className="c-combined-form__form-item">
                    <MotivationForm
                      disabled={emailIsValid === false}
                      saving={savingCombinedForm}
                      onInputChange={handleMotivationChange}
                      inputs={inputs}
                    />
                  </div>
                  {screeningQuestions && screeningQuestions.length > 0 && (
                    <div className="c-combined-form__form-item">
                      <Fieldset
                        title={t('applicationPage.extraQuestions')}
                        disabled={savingCombinedForm}
                      >
                        {screeningQuestions.map(({ node: question }, index) => (
                          <Checkbox
                            key={`${question.id}-${index}`}
                            label={question.question}
                            name={question.id}
                            id={question.id}
                            test="screeningQuestions"
                            onChange={handleChange}
                            required={question.required}
                            extraClasses="u-margin-bottom-small"
                          />
                        ))}
                      </Fieldset>
                    </div>
                  )}
                  <div className="c-combined-form__form-item">
                    <CreateEmailAlertForm
                      disabled={emailIsValid === false}
                      saving={savingCombinedForm}
                      onInputChange={handleChange}
                      inputs={inputs}
                      query={vacancy.title}
                      city={vacancy.address.city}
                    />
                  </div>
                </>
              )}
              <div className="c-combined-form__form-item">
                <PrivacyPolicyForm
                  disabled={emailIsValid === false}
                  saving={savingCombinedForm}
                  onInputChange={handleChange}
                  inputs={inputs}
                  onSubmit={submitCombinedForm}
                  submitLabel={t(`${translationKey}.submitLabel`)}
                  submitDisabled={shouldDisableSubmit()}
                />
                {shouldDisableSubmit() && (
                  <div className="c-form__hint u-margin-top-tiny">
                    {t('applicationPage.incorrectFields')}{' '}
                    {invalidFields.join(', ')}
                  </div>
                )}
              </div>
            </>
          </Form>
        </>
      )}
    </div>
  );
};

CombinedForm.propTypes = {
  translationKey: PropTypes.string.isRequired,
  shouldHideForm: PropTypes.bool,
  vacancy: PropTypes.object,
};

export default CombinedForm;
